import { didApiUrl } from '@/environment';
import { TestWebhookRequestDto, TestWebhookResponseDto } from '@/pages/agents/api';
import { authenticatedAxios } from '@/services/auth';
import { AgentStatus, AgentUpdateDTO, IAgent, IAgentData } from '@d-id/types';
import axios from 'axios';
import { deleteKnowledge } from './knowledge-api';

export const createAgent = async (agent: IAgentData, controller?: AbortController): Promise<IAgent> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/agents`, agent, { signal: controller?.signal });
    return response?.data;
};

export const updateAgent = async ({
    agentId,
    update,
    controller,
}: {
    agentId: string;
    update: AgentUpdateDTO;
    controller?: AbortController;
}): Promise<IAgent> => {
    const response = await authenticatedAxios?.patch(`${didApiUrl}/agents/${agentId}`, update, { signal: controller?.signal });
    return response?.data;
};

export const getAgent = async (agentId?: string, isAuthenticated?: boolean): Promise<IAgent | undefined> => {
    if (!agentId || !isAuthenticated) {
        return;
    }
    const response = await authenticatedAxios?.get(`${didApiUrl}/agents/${agentId}`);
    return response?.data;
};

export const getAgentByClientKey = async (agentId?: string, clientKey?: string): Promise<IAgent | undefined> => {
    if (!agentId || !clientKey) {
        return;
    }

    const response = await axios.get(`${didApiUrl}/agents/${agentId}`, {
        headers: {
            Authorization: `Client-Key ${clientKey}`,
        },
    });

    return response?.data;
};

export type GetAgentItemsResponse = {
    items: IAgent[];
    shouldPoll?: boolean;
};

export const getMyAgents = async (): Promise<IAgent[]> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/agents/me?show_conversion=true&show_templates=true`);
    const data = response?.data;

    if ('agents' in data) {
        return (data?.agents || []) as IAgent[];
    } else {
        return data as IAgent[];
    }
};

const containAgentInProcessing = (agents: IAgent[]) => {
    return agents.some(agent => !agent.status || agent.status === AgentStatus.Started);
};

export const getMyAgentsWithKnowledges = async (isAnonymous: boolean): Promise<GetAgentItemsResponse> => {
    if (isAnonymous) {
        return { items: [], shouldPoll: false };
    }
    const agents = await getMyAgents();

    // filter out campains agents
    const didAgents = agents.filter(agt => !agt.use_case || agt.use_case === 'd-id-agents');
    const shouldPoll = containAgentInProcessing(didAgents);

    return { items: didAgents, shouldPoll: shouldPoll };
};

export const deleteAgent = async (agentId: string) => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/agents/${agentId}`);
    return response?.data;
};

export const deleteAgentWithKnowledge = async (agentId: string) => {
    const agent = await deleteAgent(agentId);

    if (!agent.knowledge?.id) {
        return agent;
    }
    // Delete knowledge will also delete all attached documents
    await deleteKnowledge(agent.knowledge?.id);

    return agent;
};

export const cloneAgent = async (agentId: string) => {
    const url = `${didApiUrl}/agents/${agentId}/clone`;
    const response = await authenticatedAxios?.post(url);

    return response?.data;
};

export const testWebhook = async (payload: TestWebhookRequestDto): Promise<TestWebhookResponseDto> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/agents/webhooks/test`, payload);
    return response?.data;
};
